import { Badge, Card, CopyableEntry, Heading } from '@dittolive/anvil'
import { PageHeader } from '@portal/core/components/page-header/PageHeader'
import graphql from 'babel-plugin-relay/macro'
import ReadTheDocsLink from 'components/docsLink/ReadTheDocsLink'
import React, { Suspense, useEffect } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { useFeatureFlags } from 'utils/featureFlags'

import { AppConnectHttpsSceneQuery } from './__generated__/AppConnectHttpsSceneQuery.graphql'

export const query = graphql`
  query AppConnectHttpsSceneQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        cloudUrls {
          url
          wsEnabled
          httpEnabled
        }
      }
    }
  }
`

type ContainerProps = {
  appId: string
}

export default function AppConnectHttpsSceneContainer({
  appId,
}: ContainerProps) {
  const [queryRef, loadQuery] = useQueryLoader<AppConnectHttpsSceneQuery>(query)

  useEffect(() => {
    if (appId) {
      loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
    }
  }, [appId, loadQuery])

  if (!queryRef || !appId) {
    return null
  }

  return (
    <Suspense>
      <AppConnectHttpsScene queryRef={queryRef} />
    </Suspense>
  )
}

type CloudUrlCardProps = Pick<
  NonNullable<AppConnectHttpsSceneQuery['response']['viewer']['appById']>,
  'cloudUrls'
>

function CloudUrlCard({ cloudUrls }: CloudUrlCardProps) {
  const wsEnabled = cloudUrls[0]?.wsEnabled
  const httpEnabled = cloudUrls[0]?.httpEnabled

  return (
    <Card className="shadow-none">
      <Card.Header>
        <div className="pb-4">
          <Heading level={3} className="block">
            Big peer connection information
          </Heading>
          <p className="text-foreground-subtle font-sans text-base font-normal">
            Connect to Ditto with your cloud URL
          </p>
        </div>
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        <CopyableEntry
          title="Cloud URL Endpoint"
          testId="cloudUrl"
          value={cloudUrls[0]?.url}
          render={() =>
            cloudUrls.map((cloudUrl) => (
              <div key={cloudUrl.url}>{cloudUrl.url}</div>
            ))
          }
          isCode
        />

        <div className="flex flex-wrap items-center gap-3">
          <Badge colorScheme={httpEnabled ? 'green' : 'red'}>
            HTTP {httpEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Badge colorScheme={wsEnabled ? 'green' : 'red'}>
            Websocket {wsEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
      </Card.Body>
    </Card>
  )
}

type Props = {
  queryRef: PreloadedQuery<AppConnectHttpsSceneQuery>
}

/** Connect via HTTPS page: big peer cloud URL endpoint information. API keys
 * used to authenticate via HTTP are managed under Access > API keys.
 */
function AppConnectHttpsScene({ queryRef }: Props): React.ReactElement {
  const { viewer } = usePreloadedQuery<AppConnectHttpsSceneQuery>(
    query,
    queryRef,
  )
  const { unifiedConnectionUrl } = useFeatureFlags()

  const cloudUrls = viewer.appById?.cloudUrls || []

  return (
    <>
      <PageHeader
        title="Connect via HTTP"
        description={
          <>
            Everything you need to get connected to Ditto through our HTTP API.{' '}
            <ReadTheDocsLink docsArea="httpApi" />
          </>
        }
      />
      <div className="flex flex-col gap-8">
        {!unifiedConnectionUrl && <CloudUrlCard cloudUrls={cloudUrls} />}
      </div>
    </>
  )
}
